import React from "react"
import Box from "@mui/material/Box"
import Button from "@mui/material/Button"
import Dialog from "@mui/material/Dialog"
import DialogContent from "@mui/material/DialogContent"
import DialogTitle from "@mui/material/DialogTitle"
import Typography from "@mui/material/Typography"
import AccountCircleIcon from "@mui/icons-material/AccountCircle"
import TerminalIcon from "@mui/icons-material/Terminal"
import { loadSshSettings, tierLabel } from "./TerminalContext"

// ── Shared dir row ─────────────────────────────────────────────────────────────

function DirRow({ dir, index, active, onClick }) {
  return (
    <Box
      onClick={onClick}
      sx={{
        display: "flex", alignItems: "center", gap: 1.5,
        p: 1.25, mb: 0.75, borderRadius: 1.5, cursor: "pointer",
        border: "1px solid",
        borderColor: active ? "color-mix(in srgb, var(--app-accent) 40%, transparent)" : "rgba(var(--app-fg-rgb),0.08)",
        backgroundColor: active ? "color-mix(in srgb, var(--app-accent) 7%, transparent)" : "transparent",
        "&:hover": { borderColor: "color-mix(in srgb, var(--app-accent) 30%, transparent)", backgroundColor: "color-mix(in srgb, var(--app-accent) 4%, transparent)" },
      }}
    >
      <TerminalIcon sx={{ fontSize: 14, color: active ? "var(--app-accent)" : "rgba(var(--app-fg-rgb),0.3)", flexShrink: 0 }} />
      <Typography sx={{ fontFamily: "var(--app-font-mono)", fontSize: "0.7rem", color: active ? "var(--app-accent)" : "rgba(var(--app-fg-rgb),0.55)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {dir}
        {index === 0 && (
          <Typography component="span" sx={{ fontFamily: "var(--app-font-mono)", fontSize: "0.58rem", color: "rgba(var(--app-fg-rgb),0.25)", ml: 1 }}>
            Standard
          </Typography>
        )}
      </Typography>
    </Box>
  )
}

// ── Profile Tab Dialog (gear icon — opens agent tab) ──────────────────────────

export function ProfileTabDialog({ open, onClose, profileChoices, projectDirs, onOpen }) {
  const [selectedProfile, setSelectedProfile] = React.useState(null)
  const [selectedDir, setSelectedDir] = React.useState(null)

  React.useEffect(() => {
    if (open) { setSelectedProfile(null); setSelectedDir(null) }
  }, [open])

  const multiDirs = projectDirs.length > 1
  const chosen = profileChoices.find((p) => p.name === selectedProfile) ?? null

  return (
    <Dialog open={open} onClose={onClose} PaperProps={{ sx: { backgroundColor: "var(--app-paper)", border: "1px solid rgba(168,85,247,0.2)", borderRadius: 2, minWidth: 320 } }}>
      <DialogTitle sx={{ fontFamily: "var(--app-font-mono)", fontSize: "0.8rem", letterSpacing: "0.1em", color: "var(--feature-vault)", pb: 1 }}>
        SSH-PROFIL WECHSELN
      </DialogTitle>
      <DialogContent sx={{ pt: 0 }}>
        <Typography sx={{ fontFamily: "var(--app-font-mono)", fontSize: "0.62rem", color: "rgba(var(--app-fg-rgb),0.32)", mb: 1.5, letterSpacing: "0.1em" }}>
          PROFIL
        </Typography>
        {profileChoices.map((p) => (
          <Box
            key={p.name}
            onClick={() => setSelectedProfile(p.name)}
            sx={{
              display: "flex", alignItems: "center", gap: 1.5,
              p: 1.25, mb: 0.75, borderRadius: 1.5, cursor: "pointer",
              border: "1px solid",
              borderColor: selectedProfile === p.name ? "rgba(168,85,247,0.4)" : "rgba(var(--app-fg-rgb),0.08)",
              backgroundColor: selectedProfile === p.name ? "rgba(168,85,247,0.07)" : "transparent",
              "&:hover": { borderColor: "rgba(168,85,247,0.3)", backgroundColor: "rgba(168,85,247,0.04)" },
            }}
          >
            <AccountCircleIcon sx={{ fontSize: 15, color: p.name !== "primary" ? "var(--feature-vault)" : "var(--app-accent)", flexShrink: 0 }} />
            <Box>
              <Typography sx={{ fontFamily: "var(--app-font-mono)", fontSize: "0.72rem", color: p.name !== "primary" ? "var(--feature-vault)" : "var(--app-accent)" }}>
                {p.label}
              </Typography>
              <Typography sx={{ fontFamily: "var(--app-font-mono)", fontSize: "0.61rem", color: "rgba(var(--app-fg-rgb),0.3)" }}>
                {p.username}
              </Typography>
            </Box>
          </Box>
        ))}

        {multiDirs && (
          <>
            <Box sx={{ height: 12 }} />
            <Typography sx={{ fontFamily: "var(--app-font-mono)", fontSize: "0.62rem", color: "rgba(var(--app-fg-rgb),0.32)", mb: 1.5, letterSpacing: "0.1em" }}>
              VERZEICHNIS
            </Typography>
            {projectDirs.map((dir, i) => (
              <DirRow
                key={dir} dir={dir} index={i}
                active={(selectedDir ?? projectDirs[0]) === dir}
                onClick={() => setSelectedDir(dir)}
              />
            ))}
          </>
        )}
      </DialogContent>
      <Box sx={{ px: 3, pb: 2, display: "flex", gap: 1 }}>
        <Button size="small" onClick={onClose} fullWidth variant="outlined"
          sx={{ fontSize: "0.68rem", color: "rgba(var(--app-fg-rgb),0.38)", borderColor: "rgba(var(--app-fg-rgb),0.1)" }}>
          ABBRECHEN
        </Button>
        <Button
          size="small" onClick={() => onOpen(chosen, selectedDir ?? projectDirs[0])} fullWidth variant="outlined"
          disabled={!chosen}
          sx={{ fontSize: "0.68rem", color: "var(--feature-vault)", borderColor: "rgba(168,85,247,0.3)", "&:hover": { borderColor: "rgba(168,85,247,0.6)", backgroundColor: "rgba(168,85,247,0.06)" }, "&.Mui-disabled": { opacity: 0.35 } }}
        >
          ÖFFNEN
        </Button>
      </Box>
    </Dialog>
  )
}

// ── New Tab Dialog (+ button — opens shell tab) ────────────────────────────────

export function NewTabDialog({ open, onClose, secondaries, projectDirs, onOpen }) {
  const [selectedProfile, setSelectedProfile] = React.useState("primary")
  const [selectedDir, setSelectedDir] = React.useState(null)

  React.useEffect(() => {
    if (open) { setSelectedProfile("primary"); setSelectedDir(null) }
  }, [open])

  const cfg = loadSshSettings()
  const hasSecondary = secondaries.length > 0
  const profiles = [
    { id: "primary", label: "Primary", username: cfg.username, password: cfg.password },
    ...secondaries.map((username) => ({
      id: username, label: tierLabel(username), username, password: cfg.password,
    })),
  ]
  const multiDirs = projectDirs.length > 1
  const chosen = profiles.find((p) => p.id === selectedProfile) ?? profiles[0]

  const handleOpen = () => {
    const profile = chosen.id !== "primary" ? chosen : null
    const dir = selectedDir ?? projectDirs[0]
    onOpen(profile, dir)
  }

  return (
    <Dialog open={open} onClose={onClose} PaperProps={{ sx: { backgroundColor: "var(--app-paper)", border: "1px solid color-mix(in srgb, var(--app-accent) 18%, transparent)", borderRadius: 2, minWidth: 340 } }}>
      <DialogTitle sx={{ fontFamily: "var(--app-font-mono)", fontSize: "0.8rem", letterSpacing: "0.1em", color: "var(--app-accent)", pb: 1 }}>
        NEUER TAB
      </DialogTitle>
      <DialogContent sx={{ pt: 0 }}>
        {hasSecondary && (
          <>
            <Typography sx={{ fontFamily: "var(--app-font-mono)", fontSize: "0.62rem", color: "rgba(var(--app-fg-rgb),0.32)", mb: 1.5, letterSpacing: "0.1em" }}>
              PROFIL
            </Typography>
            {profiles.map((p) => (
              <Box
                key={p.id}
                onClick={() => setSelectedProfile(p.id)}
                sx={{
                  display: "flex", alignItems: "center", gap: 1.5,
                  p: 1.25, mb: 0.75, borderRadius: 1.5, cursor: "pointer",
                  border: "1px solid",
                  borderColor: selectedProfile === p.id ? "color-mix(in srgb, var(--app-accent) 40%, transparent)" : "rgba(var(--app-fg-rgb),0.08)",
                  backgroundColor: selectedProfile === p.id ? "color-mix(in srgb, var(--app-accent) 7%, transparent)" : "transparent",
                  "&:hover": { borderColor: "color-mix(in srgb, var(--app-accent) 30%, transparent)", backgroundColor: "color-mix(in srgb, var(--app-accent) 4%, transparent)" },
                }}
              >
                <AccountCircleIcon sx={{ fontSize: 15, color: p.id !== "primary" ? "var(--feature-vault)" : "var(--app-accent)", flexShrink: 0 }} />
                <Box>
                  <Typography sx={{ fontFamily: "var(--app-font-mono)", fontSize: "0.72rem", color: p.id !== "primary" ? "var(--feature-vault)" : "var(--app-accent)" }}>
                    {p.label}
                  </Typography>
                  <Typography sx={{ fontFamily: "var(--app-font-mono)", fontSize: "0.61rem", color: "rgba(var(--app-fg-rgb),0.3)" }}>
                    {p.username}
                  </Typography>
                </Box>
              </Box>
            ))}
          </>
        )}

        {multiDirs && (
          <>
            {hasSecondary && <Box sx={{ height: 12 }} />}
            <Typography sx={{ fontFamily: "var(--app-font-mono)", fontSize: "0.62rem", color: "rgba(var(--app-fg-rgb),0.32)", mb: 1.5, letterSpacing: "0.1em" }}>
              VERZEICHNIS
            </Typography>
            {projectDirs.map((dir, i) => (
              <DirRow
                key={dir} dir={dir} index={i}
                active={(selectedDir ?? projectDirs[0]) === dir}
                onClick={() => setSelectedDir(dir)}
              />
            ))}
          </>
        )}
      </DialogContent>
      <Box sx={{ px: 3, pb: 2, display: "flex", gap: 1 }}>
        <Button size="small" onClick={onClose} fullWidth variant="outlined"
          sx={{ fontSize: "0.68rem", color: "rgba(var(--app-fg-rgb),0.38)", borderColor: "rgba(var(--app-fg-rgb),0.1)" }}>
          ABBRECHEN
        </Button>
        <Button size="small" onClick={handleOpen} fullWidth variant="outlined"
          sx={{ fontSize: "0.68rem", color: "var(--app-accent)", borderColor: "color-mix(in srgb, var(--app-accent) 30%, transparent)", "&:hover": { borderColor: "color-mix(in srgb, var(--app-accent) 60%, transparent)", backgroundColor: "color-mix(in srgb, var(--app-accent) 6%, transparent)" } }}>
          ÖFFNEN
        </Button>
      </Box>
    </Dialog>
  )
}
